"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, MessageSquare, ChevronDown, ChevronUp, Users } from "lucide-react";
import { getFavoriteContacts } from "@/app/actions/user";
import { createOrGetConversation } from "@/app/actions/messaging";
import { UserProfilePopover } from "./user-profile-popover";
import { toast } from "sonner";

interface FavoriteContact {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
  role: string;
  firstName: string | null;
  lastName: string | null;
  profile?: {
    company: string | null;
    role: string | null;
    bio: string | null;
    licenseNumber: string | null;
    licenseState: string | null;
    yearsExperience: number | null;
    brokerage?: {
      name: string;
    } | null;
  } | null;
}

interface FavoriteContactsListProps {
  onStartConversation?: (conversationId: string) => void;
  onContactRemoved?: () => void;
}

export function FavoriteContactsList({
  onStartConversation,
  onContactRemoved,
}: FavoriteContactsListProps) {
  const [contacts, setContacts] = useState<FavoriteContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [startingConversation, setStartingConversation] = useState<string | null>(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  async function fetchContacts() {
    setLoading(true);
    try {
      const result = await getFavoriteContacts();
      if (result.error) {
        console.error("Failed to fetch contacts:", result.error);
      } else {
        setContacts(result.contacts as FavoriteContact[]);
      }
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleStartConversation(userId: string) {
    setStartingConversation(userId);
    try {
      const result = await createOrGetConversation(userId);
      if (result.error) {
        toast.error(result.error);
      } else if (result.conversation) {
        toast.success("Opening conversation");
        onStartConversation?.(result.conversation.id);
      }
    } catch (error) {
      console.error("Failed to start conversation:", error);
      toast.error("Failed to start conversation");
    } finally {
      setStartingConversation(null);
    }
  }

  function handleContactRemoved() {
    fetchContacts();
    onContactRemoved?.();
  }

  const getDisplayName = (contact: FavoriteContact) => {
    if (contact.name) return contact.name;
    if (contact.firstName && contact.lastName) {
      return `${contact.firstName} ${contact.lastName}`;
    }
    if (contact.firstName) return contact.firstName;
    return contact.email?.split("@")[0] || "Unknown User";
  };

  const getInitials = (contact: FavoriteContact) => {
    const name = getDisplayName(contact);
    const parts = name.split(" ");
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="p-4 border-b border-cyan-500/20">
        <div className="flex items-center gap-2 mb-3">
          <Star className="h-4 w-4 text-yellow-500" />
          <Skeleton className="h-4 w-24 bg-gray-800/50" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-12 rounded-full bg-gray-800/50 shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (contacts.length === 0) {
    return null; // Don't show section if no contacts
  }

  return (
    <div className="border-b border-cyan-500/20 bg-gray-900/40">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={cn(
          "w-full flex items-center justify-between p-4",
          "hover:bg-gray-800/30 transition-colors"
        )}
      >
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          <span className="text-sm font-semibold text-gray-200">
            Saved Contacts
          </span>
          <Badge
            variant="outline"
            className="text-xs border-cyan-500/30 text-cyan-400"
          >
            {contacts.length}
          </Badge>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Contacts Grid */}
      {!collapsed && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-3">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className={cn(
                  "group relative flex flex-col items-center",
                  "p-3 rounded-lg",
                  "bg-gray-800/40 hover:bg-gray-800/60",
                  "border border-border/5 hover:border-cyan-500/30",
                  "transition-all duration-200",
                  "min-w-[100px] max-w-[120px]"
                )}
              >
                {/* Avatar with Popover */}
                <UserProfilePopover
                  user={{
                    id: contact.id,
                    name: contact.name,
                    email: contact.email,
                    image: contact.image,
                  }}
                  isFavorite={true}
                  onFavoriteChange={handleContactRemoved}
                  onStartConversation={onStartConversation}
                  side="bottom"
                  align="center"
                >
                  <div className="relative">
                    <Avatar className="h-12 w-12 ring-2 ring-white/10 hover:ring-cyan-500/50 transition-all">
                      <AvatarImage
                        src={contact.image || undefined}
                        alt={getDisplayName(contact)}
                      />
                      <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white text-sm font-semibold">
                        {getInitials(contact)}
                      </AvatarFallback>
                    </Avatar>
                    {/* Favorite indicator */}
                    <div className="absolute -top-1 -right-1 h-5 w-5 bg-yellow-500 rounded-full border-2 border-gray-900 flex items-center justify-center shadow-lg shadow-yellow-500/50">
                      <Star className="h-3 w-3 text-gray-900 fill-gray-900" />
                    </div>
                  </div>
                </UserProfilePopover>

                {/* Name */}
                <span className="mt-2 text-xs text-gray-300 text-center truncate w-full">
                  {getDisplayName(contact)}
                </span>

                {/* Quick Message Button */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartConversation(contact.id);
                  }}
                  disabled={startingConversation === contact.id}
                  className={cn(
                    "mt-2 h-7 px-2",
                    "bg-cyan-500/10 hover:bg-cyan-500/20",
                    "text-cyan-400 hover:text-cyan-300",
                    "border border-cyan-500/30 hover:border-cyan-500/50",
                    "transition-all duration-200 text-xs"
                  )}
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  {startingConversation === contact.id ? "..." : "Message"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
